import React, { useEffect, useRef, useState } from 'react';
import Drawer from "../Drawer/Drawer";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck, faPlus, faRemove, faSquareInfo } from '@fortawesome/pro-solid-svg-icons';
import Button, { BUTTON_VARIANTS } from '../UI/Button/Button';
import Group from '../UI/Group/Group';
import { fetchReportReasons, REPORT_REASONS_KEY } from '../../../api/public-voissa/reports';
import { useQuery } from '@tanstack/react-query';
import useUpload from '../../../hooks/useUpload';
import useReport from '../../../hooks/useReport';
import InfiniteScrollLoading from '../InfiniteScrollLoading/InfiniteScrollLoading';

const labels = [
  "Raison de votre signalement",
  "Finaliser le signalement",
  "Signalement envoyé"
];

const StepSelectReason = ({ targetKind = "", onSelect }) => {
  const { data: reportReasons, isLoading } = useQuery(
    [REPORT_REASONS_KEY, targetKind],
    () => fetchReportReasons({ kind : targetKind }),
    { staleTime: 0 }
  );

  return (
    <div className="report-step">
      {isLoading ? <InfiniteScrollLoading/> : (
        <ul className="report-reason-list">
          {reportReasons.map(reason => (
            <li key={reason._id} onClick={() => onSelect(reason)}>
              {reason.text} <FontAwesomeIcon icon={faArrowRight} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const StepSubmitReport = ({ targetKind = "", targetId, targetUserId, selectedReason, onBack, goNext }) => {
  const [description, setDescription] = useState('');
  const refUploadInput = useRef();
  const textRef = useRef();
  const { data: dataUploads, uploads, resetUpload } = useUpload("/reports/uploads");
  const { handleReport, isLoading: isLoadingReport, error : errorReport, isError : isErrorReport } = useReport({ targetKind, targetId, targetUserId, onSuccess: () => goNext() });

  const handleSubmit = () => {
    handleReport(description, selectedReason?._id, dataUploads?.res?._id);
  };

  const handleFiles = async () => {
    const input = refUploadInput.current;
    if (!input?.files?.length) return;
    await uploads(input.files);
    input.value = null; // permet de re-upload le même fichier
  };

  useEffect(() => {
    textRef.current?.focus();
  }, []);

  
  return (
    <div className="report-step ">
      <p className="description">
        Vous pouvez ajouter des précisions ou une capture d'écran pour nous aider à mieux comprendre.
      </p>
      {selectedReason?.hint && (
        <p className="hint">
          <FontAwesomeIcon icon={faSquareInfo} className="hint-info" /> {selectedReason?.hint}
        </p>
      )}
      <textarea
        className="report-textarea"
        placeholder="Ajoutez des détails pour nous aider à comprendre (facultatif)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        ref={textRef}
      />
      <div className="report-upload">
        {!dataUploads?.preview && (<label className="file-label">
          <Button onClick={() => refUploadInput.current?.click()} full>
            <FontAwesomeIcon icon={faPlus} /> Ajouter une capture (facultatif)
          </Button>
          <input
            type="file"
            accept="image/jpeg, image/png"
            ref={refUploadInput}
            onChange={handleFiles}
          />
        </label>)}

        {dataUploads?.preview && (
          <div className={`upload-preview-wrapper ${dataUploads?.isLoading ? 'is-loading' : ''}`}>
            <img src={dataUploads.preview} alt="Aperçu" className="upload-preview-image" />
            {dataUploads?.isLoading && <div className="upload-spinner" />}
            {!dataUploads?.isLoading && (
              <Button
                className="upload-remove-button"
                onClick={resetUpload}
                onlyIcon
                small
              >
                <FontAwesomeIcon icon={faRemove} />
              </Button>
            )}
          </div>
        )}
      </div>

      {isErrorReport && (
        <div className="report-error">
          {errorReport?.message || "Une erreur est survenue, veuillez réessayer."}
        </div>
      )}

      <Group center split>
        <Button onClick={onBack} variant={BUTTON_VARIANTS.OUTLINE}>Retour</Button>
        <Button onClick={handleSubmit} variant={BUTTON_VARIANTS.REVERSE} loading={isLoadingReport}>Envoyer</Button>
      </Group>
    </div>
  );
};

const StepThankYou = ({ onClose }) => (
  <div className="report-step final">
    <FontAwesomeIcon icon={faCheck} className="check" />
    <p className="description">
        Votre signalement a été enregistré. <br />Il sera examiné dans les plus brefs délais par notre équipe de modération.
    </p>
    <Button onClick={onClose} variant={BUTTON_VARIANTS.OUTLINE}>Fermer</Button>
  </div>
);

const Report = ({ targetKind = "", targetId, targetUserId, handleClose = () => {}, step: externalStep, setStep: externalSetStep }) => {
  const [internalStep, setInternalStep] = useState(0);
  const [selectedReason, setSelectedReason] = useState(null);

  const step = typeof externalStep === 'number' ? externalStep : internalStep;
  const setStep = typeof externalSetStep === 'function' ? externalSetStep : setInternalStep;

  const steps = [
    <StepSelectReason key="step-0" targetKind={targetKind} onSelect={(reason) => { setSelectedReason(reason); setStep(1); }} />,
    <StepSubmitReport key="step-1" selectedReason={selectedReason} onBack={() => setStep(0)} goNext={() => setStep(2)} targetKind={targetKind} targetId={targetId} targetUserId={targetUserId} />,
    <StepThankYou key="step-2" onClose={() => { setStep(0); handleClose(); }} />
  ];

  return <div className="report-container">{steps[step]}</div>;
};

export const ReportInDrawer = ({ targetKind = "", targetId, targetUserId, open = false, handleClose = () => {} }) => {
  const [step, setStep] = useState(0);
  const close = () => { setStep(0); handleClose(); };

  return (
    <Drawer
      open={open}
      handleClose={close}
      headContent={labels[step] || "Signaler"}
      small
    >
      <Report targetKind={targetKind} targetId={targetId} targetUserId={targetUserId} handleClose={close} step={step} setStep={setStep} />
    </Drawer>
  );
};

export default Report;
